import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { X, Check } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { Button, Input, Textarea } from "@/components/ui";
import {
  INDUSTRY_OPTIONS,
  COMPANY_SIZE_OPTIONS,
  JURISDICTION_OPTIONS,
  ACCENT_COLOR_PRESETS,
} from "./WorkspaceSettingsForm";

interface CreateWorkspaceModalProps {
  open: boolean;
  onClose: () => void;
}

export function CreateWorkspaceModal({
  open,
  onClose,
}: CreateWorkspaceModalProps) {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  // useMutation used here because workspaces.create doesn't use makeHandler,
  // so useApiMutation's Result unwrapping returns unknown.
  const createWorkspace = useMutation(api.workspaces.create);

  const [name, setName] = useState("");
  const [industry, setIndustry] = useState(INDUSTRY_OPTIONS[0]);
  const [companySize, setCompanySize] = useState(COMPANY_SIZE_OPTIONS[0]);
  const [jurisdiction, setJurisdiction] = useState(JURISDICTION_OPTIONS[0]);
  const [companyDescription, setCompanyDescription] = useState("");
  const [accentColor, setAccentColor] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = name.trim().length > 0 && !submitting;

  function resetForm() {
    setName("");
    setIndustry(INDUSTRY_OPTIONS[0]);
    setCompanySize(COMPANY_SIZE_OPTIONS[0]);
    setJurisdiction(JURISDICTION_OPTIONS[0]);
    setCompanyDescription("");
    setAccentColor("");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      const result = await createWorkspace({
        name: name.trim(),
        industry,
        companySize,
        jurisdiction,
        companyName: name.trim(),
        accentColor: accentColor || undefined,
      });

      toast.success(t("workspace.formActions.saved"));
      resetForm();
      onClose();

      navigate({
        to: "/$workspaceSlug/settings/workspace",
        params: { workspaceSlug: result.slug },
      });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("workspace.formActions.saveFailed"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const selectClassName =
    "h-9 w-full rounded-button border border-border bg-card px-2.5 text-[14px] text-primary outline-none cursor-pointer hover:bg-card-hover transition-colors";

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              />
            </Dialog.Overlay>

            <Dialog.Content asChild>
              <motion.div
                className="fixed inset-0 z-[60] flex items-center justify-center focus:outline-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  className="w-full max-w-[480px] max-h-[85vh] rounded-[12px] bg-card shadow-xl flex flex-col overflow-hidden"
                  initial={{ y: 24, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 24, opacity: 0 }}
                  transition={{
                    type: "spring",
                    damping: 25,
                    stiffness: 300,
                  }}
                >
                  <div className="flex items-center justify-between px-6 pt-5 pb-3">
                    <Dialog.Title className="text-[18px] font-semibold text-primary">
                      {t("workspace.create.title")}
                    </Dialog.Title>
                    <Dialog.Close asChild>
                      <button
                        type="button"
                        className="rounded-md p-1 text-muted hover:text-primary hover:bg-card-hover transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </Dialog.Close>
                  </div>

                  <form
                    onSubmit={handleSubmit}
                    className="flex-1 overflow-y-auto px-6 pb-2"
                  >
                    <div className="space-y-4">
                      <div>
                        <span className="block text-[13px] font-medium text-primary mb-1.5">
                          {t("workspace.create.fields.name")}
                        </span>
                        <Input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder={t(
                            "workspace.create.fields.namePlaceholder",
                          )}
                          autoFocus
                        />
                      </div>

                      <div>
                        <label className="block">
                          <span className="block text-[13px] font-medium text-primary mb-1.5">
                            {t("workspace.fields.industry")}
                          </span>
                          <select
                            value={industry}
                            onChange={(e) => setIndustry(e.target.value)}
                            className={selectClassName}
                          >
                            {INDUSTRY_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>

                      <div>
                        <label className="block">
                          <span className="block text-[13px] font-medium text-primary mb-1.5">
                            {t("workspace.create.fields.teamSize")}
                          </span>
                          <select
                            value={companySize}
                            onChange={(e) => setCompanySize(e.target.value)}
                            className={selectClassName}
                          >
                            {COMPANY_SIZE_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>

                      <div>
                        <label className="block">
                          <span className="block text-[13px] font-medium text-primary mb-1.5">
                            {t("workspace.create.fields.location")}
                          </span>
                          <select
                            value={jurisdiction}
                            onChange={(e) => setJurisdiction(e.target.value)}
                            className={selectClassName}
                          >
                            {JURISDICTION_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>

                      <div>
                        <span className="block text-[13px] font-medium text-primary mb-1.5">
                          {t("workspace.create.fields.description")}
                        </span>
                        <Textarea
                          value={companyDescription}
                          onChange={(e) => {
                            if (e.target.value.length <= 500) {
                              setCompanyDescription(e.target.value);
                            }
                          }}
                          placeholder={t(
                            "workspace.create.fields.descriptionPlaceholder",
                          )}
                          rows={3}
                        />
                        <p className="text-xs text-muted text-right mt-1">
                          {companyDescription.length}/500
                        </p>
                      </div>

                      <div>
                        <span className="block text-[13px] font-medium text-primary mb-1.5">
                          {t("workspace.fields.accentColor")}
                        </span>
                        <div className="flex items-center gap-2">
                          {ACCENT_COLOR_PRESETS.map(({ hex, label }) => {
                            const isSelected = accentColor === hex;
                            return (
                              <button
                                key={hex}
                                type="button"
                                title={label}
                                onClick={() =>
                                  setAccentColor(isSelected ? "" : hex)
                                }
                                className="relative w-8 h-8 rounded-full border-2 transition-all"
                                style={{
                                  backgroundColor: hex,
                                  borderColor: isSelected ? hex : "transparent",
                                  boxShadow: isSelected
                                    ? `0 0 0 2px var(--color-page), 0 0 0 4px ${hex}`
                                    : undefined,
                                }}
                              >
                                {isSelected && (
                                  <Check className="absolute inset-0 m-auto w-4 h-4 text-white" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </form>

                  <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border/50">
                    <Button variant="secondary" onClick={onClose}>
                      {t("workspace.create.cancel")}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSubmit}
                      disabled={!canSubmit}
                      loading={submitting}
                    >
                      {t("workspace.create.submit")}
                    </Button>
                  </div>
                </motion.div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
